import {
  faFilter,
  faPlus,
  faTimes,
  faCheck,
  faUserSlash,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import axios, { AxiosError } from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import SearchBox from "components/common/SearchBox";
import FilterOffCanvas, {
  FilterOption,
} from "components/common/FilterOffCanvas";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { ChangeEvent, useEffect, useState } from "react";
import { Spinner, Card, Badge } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import UsuarioTable, {
  usuarioTableColumns,
  UsuarioTableType,
} from "components/tables/UsuarioTable";
import { usuariosBreadcrumbItems } from "data/usuarios";
import AccessDeniedOverlay from "components/modules/common/AccessDeniedOverlay";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { useToast } from "providers/ToastProvider";

// Define API response type
interface UsuarioApiResponse {
  data: UsuarioTableType[];
  isSuccess: boolean;
  message: string;
  total?: number;
  pageNumber?: number;
  pageSize?: number;
}

// Interface para os filtros ativos
interface ActiveFilters {
  userStatus: string; // 'active', 'deleted', 'all'
}

// Interface para o estado de filtros consolidado
interface FilterState {
  active: ActiveFilters;
  pending: ActiveFilters | null;
  filtersPending: boolean;
}

// Função para buscar usuários da API
const fetchUsuarios = async (
  filter?: string,
  filters?: ActiveFilters,
  pageIndex: number = 1,
  pageSize: number = 10
) => {
  try {
    // Determinar se incluir usuários excluídos
    const incluirExcluidos =
      filters?.userStatus === "all" || filters?.userStatus === "deleted";
    // Determinar se filtrar para mostrar apenas excluídos
    const apenasExcluidos = filters?.userStatus === "deleted";

    const response = await axios.get<UsuarioApiResponse>("/api/usuario", {
      params: {
        ...(filter && { filter }), // Changed from filters to filter to match backend
        incluirExcluidos,
        apenasExcluidos,
        pageIndex,
        pageSize,
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Erro ao buscar usuários:", error);
    throw error;
  }
};

const Usuarios = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { showToast } = useToast();
  const [showFilterCanvas, setShowFilterCanvas] = useState<boolean>(false);

  // Add pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Estado consolidado para filtros
  const [filterState, setFilterState] = useState<FilterState>({
    active: { userStatus: "active" },
    pending: null,
    filtersPending: false,
  });

  // Use the permission modal hook
  const { getQueryRetryHandler } = usePermissionModal();

  // Opções de filtro para o componente FilterOffCanvas - simplificadas para status do usuário
  const filterOptions: FilterOption[] = [
    {
      id: "userStatus",
      label: "Status do Usuário",
      type: "radioCard",
      options: [
        {
          value: "active",
          label: "Ativos",
          description: "Apenas usuários ativos",
          icon: faCheck,
        },
        {
          value: "deleted",
          label: "Excluídos",
          description: "Apenas usuários excluídos",
          icon: faUserSlash,
        },
        {
          value: "all",
          label: "Todos",
          description: "Ativos e excluídos",
          icon: faUsers,
        },
      ],
    },
  ];

  const location = useLocation();
  const navigate = useNavigate();

  // Conta quantos filtros estão ativos
  const activeFilterCount = filterState.active.userStatus !== "active" ? 1 : 0;

  // Verificar tanto parâmetros de URL quanto state para mensagens de sucesso
  useEffect(() => {
    // Check if we need to refresh data coming from edit screen
    if (location.state && location.state.needsRefresh) {
      dataQuery.refetch();

      // Clear the state to avoid repeated refreshes
      navigate("/cadastros/usuarios", {
        replace: true,
        state: {}, // Limpar o state
      });

      return;
    }

    // Verificar se há uma mensagem de exclusão
    if (location.state && location.state.deleteSuccess) {
      const { deletedName, deletedCode } = location.state;
      showToast(<>Usuário <b>{deletedCode}</b> foi excluído com sucesso!</>, "success");

      // Limpar o state para evitar que a mensagem apareça novamente após navegação
      navigate("/cadastros/usuarios", {
        replace: true,
        state: {}, // Limpar o state
      });

      //chamar a função de refetch para atualizar a tabela
      dataQuery.refetch();
      return;
    }

    // Primeiro verifica se há state com informações de sucesso
    if (location.state && location.state.successCode) {
      const { successCode, isNew } = location.state;
      const action = isNew ? "cadastrado" : "atualizado";
      showToast(<>Usuário <b>{successCode}</b> foi {action} com sucesso!</>, "success");

      // Limpar o state para evitar que a mensagem apareça novamente após navegação
      navigate("/cadastros/usuarios", {
        replace: true,
        state: {}, // Limpar o state
      });

      dataQuery.refetch();
      return;
    }
  }, [location, navigate]);

  // Usar react-query para buscar dados com cache e revalidação
  const dataQuery = useQuery<UsuarioApiResponse>({
    queryKey: [
      "usuarios",
      searchTerm,
      filterState.active,
      pagination.pageIndex,
      pagination.pageSize
    ],
    queryFn: () => {
      return fetchUsuarios(
        searchTerm,
        filterState.active,
        pagination.pageIndex + 1,
        pagination.pageSize
      );
    },
    placeholderData: keepPreviousData,
    staleTime: 30000, // Dados válidos por 30 segundos antes de revalidar
    retry: getQueryRetryHandler({
      queryKey: ["usuarios", searchTerm, filterState.active, pagination.pageIndex, pagination.pageSize],
      defaultMessage: "Você não possui permissão para consultar usuários."
    }),
  });

  // Explicitly refetch when pagination changes
  useEffect(() => {
    dataQuery.refetch();
  }, [pagination.pageIndex, pagination.pageSize]);

  const table = useReactTable({
    data: dataQuery.data?.data || [],
    columns: usuarioTableColumns,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: dataQuery.data?.total
      ? Math.ceil(dataQuery.data.total / pagination.pageSize)
      : 1,
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Reset pagination when search term or filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, [searchTerm, filterState.active]);

  const handleApplyFilters = (filters: Record<string, any>) => {
    // Apply filters immediately instead of storing as pending
    setFilterState((prev) => ({
      ...prev,
      active: filters as ActiveFilters,
      pending: null,
      filtersPending: false,
    }));

    // Reset to first page when applying new filters
    setPagination(prev => ({ ...prev, pageIndex: 0 }));

    // Close the filter canvas
    setShowFilterCanvas(false);

    // Refetch data with new filters
    dataQuery.refetch();
  };

  const handleClearFilters = () => {
    setFilterState((prev) => ({
      ...prev,
      active: { userStatus: "active" },
      pending: null,
      filtersPending: false,
    }));
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
    dataQuery.refetch();
  };

  if (dataQuery.isLoading) {
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Carregando...</span>
      </Spinner>
    );
  }

  if (dataQuery.error) {
    const error = dataQuery.error as AxiosError;
    if (error.response?.status === 403) {
      return (
        <div>
          <PageBreadcrumb items={usuariosBreadcrumbItems} />
          <div className="mb-9">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="mb-0">Usuários</h2>
              <Button
                variant="primary"
                as={Link}
                to="/cadastros/usuarios/novo"
                className="px-4 py-2"
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Novo Usuário
              </Button>
            </div>

            <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mb-3">
              <div className="d-flex gap-2 flex-grow-1">
                <SearchBox
                  placeholder="Pesquisar usuários por código, nome ou email..."
                  onChange={() => { }}
                  className="flex-grow-1"
                  disabled={true}
                />
              </div>
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="outline-primary"
                  className="px-3 py-2 d-flex align-items-center"
                  onClick={() => { }}
                  disabled={true}
                >
                  <FontAwesomeIcon icon={faFilter} className="me-2" />
                  Filtros
                </Button>
              </div>
            </div>

            <AccessDeniedOverlay message="Você não tem permissão para consultar ou visualizar usuários.">
              <div
                className="position-relative"
                style={{
                  filter: "blur(8px)",
                  pointerEvents: "none",
                  userSelect: "none",
                }}
              >
                <Card className="border-0 shadow-sm">
                  <Card.Body className="p-0">
                    <div className="mx-n4 px-4 rounded-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>Código</th>
                              <th>Nome</th>
                              <th>Email</th>
                              <th>Status</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Array(5)
                              .fill(0)
                              .map((_, i) => (
                                <tr key={i}>
                                  <td>{i + 100}</td>
                                  <td>Usuário Exemplo</td>
                                  <td>usuario{i + 1}@exemplo.com</td>
                                  <td>Ativo</td>
                                  <td>-</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </AccessDeniedOverlay>
          </div>
        </div>
      );
    }

    return (
      <div className="text-center py-5 text-danger">
        {(dataQuery.error as Error).message}
      </div>
    );
  }

  // Display error message if isSuccess is false
  if (dataQuery.data && !dataQuery.data.isSuccess) {
    return (
      <div className="alert alert-danger">
        Erro ao carregar usuários:{" "}
        {dataQuery.data.message || "Ocorreu um erro desconhecido."}
      </div>
    );
  }

  return (
    <div>
      <PageBreadcrumb items={usuariosBreadcrumbItems} />
      <div className="mb-9">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">Usuários</h2>
          <Button
            variant="primary"
            as={Link}
            to="/cadastros/usuarios/novo"
            className="px-4 py-2"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Novo Usuário
          </Button>
        </div>

        <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mb-3">
          <div className="d-flex gap-2 flex-grow-1">
            <SearchBox
              placeholder="Pesquisar usuários por código, nome ou email..."
              onChange={handleSearchInputChange}
              className="flex-grow-1"
            />
          </div>
          <div className="d-flex align-items-center gap-2">
            {activeFilterCount > 0 && (
              <Badge
                bg="light"
                text="dark"
                className="d-flex align-items-center py-2 px-3 border shadow-sm"
                style={{ borderRadius: "50px" }}
              >
                <span>
                  {filterState.active.userStatus === "deleted" &&
                    "Mostrando excluídos"}
                  {filterState.active.userStatus === "all" && "Mostrando todos"}
                </span>
                <Button
                  variant="link"
                  className="p-0 ms-2 text-body-secondary border-0"
                  onClick={handleClearFilters}
                  style={{ fontSize: "14px" }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              </Badge>
            )}

            <Button
              variant={activeFilterCount > 0 ? "primary" : "outline-primary"}
              className="px-3 py-2 d-flex align-items-center"
              onClick={() => setShowFilterCanvas(true)}
            >
              <FontAwesomeIcon icon={faFilter} className="me-2" />
              Filtros
              {activeFilterCount > 0 && (
                <Badge
                  bg="light"
                  text="primary"
                  className="ms-2 d-flex align-items-center justify-content-center"
                  style={{ borderRadius: "50%", width: "20px", height: "20px" }}
                >
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <AdvanceTableProvider {...table}>
              <div className="mx-n4 px-4 rounded-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                <UsuarioTable />
              </div>
            </AdvanceTableProvider>
          </Card.Body>
        </Card>
      </div>

      <FilterOffCanvas
        show={showFilterCanvas}
        onHide={() => setShowFilterCanvas(false)}
        onApplyFilters={handleApplyFilters}
        title="Filtros de Usuários"
        filterOptions={filterOptions}
        activeFilters={filterState.active}
      />
    </div>
  );
};

export default Usuarios;
